import {useCallback, useEffect, useRef} from 'react';
import {getAllFocusableElements, resetFocusToRoot} from './helpers';
import {FocusManagementType} from './types';

// Marks the element a dialog wants focus on when it opens: the labelled dialog
// element itself (tabindex="-1"). Screen readers then announce "<title>, dialog"
// once. Focusing the title as well would announce it twice, focusing a container
// reads everything inside it, and focusing the first control can open the
// mobile keyboard or be activated by a key held down
export const INITIAL_FOCUS_ATTRIBUTE = 'data-initial-focus';
const INITIAL_FOCUS_SELECTOR = `[${INITIAL_FOCUS_ATTRIBUTE}]`;

const getInitialFocusElement = (container: HTMLElement) =>
  container.closest<HTMLElement>(INITIAL_FOCUS_SELECTOR) ??
  container.querySelector<HTMLElement>(INITIAL_FOCUS_SELECTOR);

// A container that is itself focusable (tabindex) is listed first, but focusing
// it makes screen readers read everything inside it, so the controls inside
// are preferred and the container is only used when it has none. Elements that
// only have a role (e.g. `role="button"` without a tabindex) are listed too
// but cannot take focus, so they are not targets
const getFocusTargets = (container: HTMLElement) => {
  const focusable = getAllFocusableElements(container);
  const controls = focusable.filter(
    element => element !== container && element.tabIndex >= 0,
  );

  return controls.length > 0 ? controls : focusable;
};

/**
 * Implements focus trapping behavior for popovers, and other overlay components.
 * It ensures that keyboard focus remains within a specific container when it's active,
 * improving accessibility for keyboard users. This hook is mostly for internal use
 * in this package, prefer using `useFocusManagement` since it ensures that an opened
 * popover is added to the stack of opened popovers, and prevents adding the keyboard
 * listeners to closed popovers.
 *
 * @param param0
 */
export const useFocusTrap = ({
  isOpen,
  containerRef,
  disable,
  openerElement,
}: FocusManagementType) => {
  const shouldFocusOpenerOnClose = useRef(false);
  // A popover that is covered by another one, e.g. the size selection under
  // the size reminder, must not pull focus out of the top most popover when it closes.
  const isCoveredRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      isCoveredRef.current = !!disable;

      if (disable) {
        return;
      }

      const container = containerRef?.current;

      if (!container || shouldFocusOpenerOnClose.current) {
        return;
      }

      const target =
        getInitialFocusElement(container) ?? getFocusTargets(container)[0];

      if (!target) {
        return;
      }

      target.focus();

      // A target that is hidden or not yet rendered cannot take focus. The
      // container still can, which beats leaving focus behind the dialog
      if (document.activeElement !== target && container.tabIndex >= 0) {
        container.focus();
      }

      shouldFocusOpenerOnClose.current = true;
    } else {
      if (
        shouldFocusOpenerOnClose.current &&
        !isCoveredRef.current &&
        openerElement
      ) {
        // `body` isn't naturally focusable, so a plain `focus()` call on it
        // is a no-op — it needs the same reset as having no opener at all.
        if (openerElement === document.body) {
          resetFocusToRoot();
        } else {
          openerElement.focus({preventScroll: true});
        }
      }

      // Without this, focus only moves into the popover on its first open.
      shouldFocusOpenerOnClose.current = false;
    }
  }, [isOpen, disable, containerRef.current]);

  const tabNavigation = useCallback(
    (e: KeyboardEvent) => {
      if (disable) {
        return null;
      }

      shouldFocusOpenerOnClose.current = true;

      const isTabPressed = e.key === 'Tab' || e.keyCode === 9;

      if (!isTabPressed) {
        return null;
      }

      const container = containerRef.current;

      if (!container) {
        return null;
      }

      const focusableElements = getFocusTargets(container);

      if (focusableElements.length === 0) {
        return;
      }

      const firstFocusableElement = focusableElements[0];
      const lastFocusableElement =
        focusableElements[focusableElements.length - 1];

      // The entry point is not a tab stop of the trap, and nor is the container,
      // so going back from them wraps to the last control instead of leaving
      const entryPoint = getInitialFocusElement(container);
      const isOnEntryPoint =
        document.activeElement === container ||
        (!!entryPoint && document.activeElement === entryPoint);

      if (!container?.contains(document.activeElement) && !isOnEntryPoint) {
        e.preventDefault();
        firstFocusableElement?.focus();
      } else if (isOnEntryPoint && !e.shiftKey) {
        e.preventDefault();
        firstFocusableElement?.focus();
      } else if (e.shiftKey) {
        if (
          document.activeElement === firstFocusableElement ||
          isOnEntryPoint
        ) {
          e.preventDefault();
          lastFocusableElement?.focus();
        }
      } else {
        if (document.activeElement === lastFocusableElement) {
          e.preventDefault();
          firstFocusableElement?.focus();
        }
      }
    },
    [containerRef.current, disable],
  );

  useEffect(() => {
    if (isOpen && !disable) {
      document.addEventListener('keydown', tabNavigation);
    }

    return () => {
      document.removeEventListener('keydown', tabNavigation);
    };
  }, [isOpen, disable, containerRef.current]);
};
